import { AxiosPromise } from "axios";
import ExportButton from "components/filters/Export/ExportButton";
import { FilterProvider, FilterValues, type FilterProviderRef } from "components/filters/FilterContext";
import FilterHeader from "components/filters/FilterHeader";
import { appendFilterValuesToQueryString } from "components/filters/mappingUtils";
import FilterPresetButton from "components/filters/preset/FilterPresetButton";
import { NoDataAvailableDiv, NoDataAvailableWarning } from "components/NoDataAvailableDiv";
import { Loading } from "components/panels";
import Panel from "components/panels/panel";
import PanelSegment from "components/panels/PanelSegment/PanelSegment";
import React, { useRef, useState } from "react";
import { useCustomQuery, useElementHeight } from "utilities/hookutils";
import { CalendarTemplate } from "./CalendarTemplate";
import { QuarterlyAppointmentList } from "./QuarterlyAppointmentList";
import { useIsExternal } from "common/SessionContext";

interface PlanningTemplateProps<
  TInstruction,
  TItem,
  TFilter
> {
  filterSet: server.dto.FilterSets;
  initialInstruction: TInstruction;
  defaultFilters: TFilter;
  fetchFunction: (instruction: TInstruction) => AxiosPromise<server.dto.ApiResponseWrapperWithRequest<TItem[], TFilter>>;
  mapFilterValuesToInstruction: (filterSet: server.dto.FilterSets, filter: FilterValues) => TInstruction;
  mapResponseToFilterValues: (response: TFilter, values: FilterValues) => Partial<FilterValues>;
  filterComp: JSX.Element;
  paginationComp: JSX.Element;
  maxEntriesPerColumn?: number;
  quarterly?: boolean;
  filterPreset?: {
    listApi: () => AxiosPromise<server.dto.ApiResponseWrapper<server.dto.FilterPreset<TInstruction>[]>>
    mutateApi: (instruction: server.dto.CreateFilterPresetInstruction<TInstruction>) => AxiosPromise<any>
  }
  exportOptions?: {
    canExportList: boolean
    exportOptionsApi: (instruction: TInstruction) => AxiosPromise<server.dto.ApiResponseWrapper<server.dto.ExportOption[]>>
  }
}

export const planningTemplateQueryKey = "planningTemplateQueryKey";

export const PlanningTemplate = <TInstruction, TItem, TFilter>({
  filterSet,
  initialInstruction,
  defaultFilters,
  fetchFunction,
  mapFilterValuesToInstruction,
  mapResponseToFilterValues,
  filterComp,
  paginationComp,
  maxEntriesPerColumn,
  quarterly,
  filterPreset,
  exportOptions,
}: PlanningTemplateProps<TInstruction, TItem, TFilter>) => {
  const filterRef = useRef<FilterProviderRef>(null);
  const [instruction, onInstructionChange] = useState<TInstruction>({ ...initialInstruction, cache: filterSet });
  const [initialLoading, setInitialLoading] = useState(true);
  const isExternal = useIsExternal();

  const api = useCustomQuery({
    queryFn: async () => {
      return await fetchFunction(instruction);
    },
    queryKey: [planningTemplateQueryKey, filterSet, instruction],
    onSuccess: (response) => {
      if (initialLoading) {
        filterRef.current.setDefaultValues((prev: FilterValues) => {
          return mapResponseToFilterValues(defaultFilters, prev);
        });
      }
      const filtersWithValues = filterRef.current.updateValues((prev: FilterValues) => {
        return mapResponseToFilterValues(response.data.request, prev);
      });
      if (initialLoading) {
        appendFilterValuesToQueryString(filtersWithValues);
      }
      setInitialLoading(false);
    },
    cacheTime: 0
  });

  const onFilterPresetApply = async (preset: server.dto.FilterPreset<TInstruction>) => {
    if (preset.filter == null) {
      filterRef.current.reset();
      return;
    }

    onInstructionChange({ ...preset.filter, cache: filterSet });
  };

  const onFilterChange = (filter: FilterValues, filtersWithValues: Partial<FilterValues>) => {
    const newInstruction = mapFilterValuesToInstruction(filterSet, filter);
    appendFilterValuesToQueryString(filtersWithValues);
    onInstructionChange(newInstruction);
  };

  const items = api.data?.data.data ?? [];
  const errors = api.data?.data.errors ?? [];
  const pagination = api.data?.data.pagination;
  const isLoading = initialLoading || api.isLoading;

  const fetchDayFunction = isExternal ?
    (day: moment.Moment) => window.CoreApi.Planning.PlanningByDayForExternal({ ...instruction, date: day.format("YYYY-MM-DD") as any }) :
    (day: moment.Moment) => window.CoreApi.Planning.PlanningByDayForInternal({ ...instruction, date: day.format("YYYY-MM-DD") as any });

  return <Panel
    style={{ minWidth: 800 }}
    contentCssClass="flex-column panel-gap">
    <FilterProvider
      ref={filterRef}
      onFilterChange={onFilterChange}
    >
      <div className="flex gap-big">
        <FilterHeader.Filters isLoading={initialLoading}>
          <div className="selectiontool-header-filters">
            {filterComp}
          </div>
          {filterPreset && <FilterPresetButton
            cacheKey={filterSet}
            filterSet={filterSet}
            instruction={instruction}
            onApplyPreset={onFilterPresetApply}
            fetchFunction={fetchFunction}
            mapResponseToFilterValues={mapResponseToFilterValues}
            listApi={filterPreset.listApi}
            mutateApi={filterPreset.mutateApi}
          />}
        </FilterHeader.Filters>
      </div>

      {quarterly ?
        <QuarterlyLayout
          items={items as unknown as server.dto.PlannerDay[]}
          errors={errors}
          instruction={instruction}
          initialLoading={initialLoading}
          isLoading={isLoading}
          paginationComp={paginationComp}
          pagination={pagination}
          exportOptions={exportOptions}
        /> :
        <CalendarLayout
          filterSet={filterSet}
          items={items as unknown as server.dto.AppointmentsForDay[]}
          fetchDayFunction={fetchDayFunction}
          maxEntriesPerColumn={maxEntriesPerColumn}
          initialLoading={initialLoading}
          isLoading={isLoading}
          paginationComp={paginationComp}
          pagination={pagination}
        />}
    </FilterProvider>
  </Panel>
};

interface QuarterlyLayoutProps<TInstruction> {
  items: server.dto.PlannerDay[];
  instruction: TInstruction;
  initialLoading: boolean;
  errors: server.dto.ApiError[];
  isLoading: boolean;
  paginationComp: JSX.Element;
  pagination: server.dto.PaginationData;
  exportOptions?: {
    canExportList: boolean
    exportOptionsApi: (instruction: TInstruction) => AxiosPromise<server.dto.ApiResponseWrapper<server.dto.ExportOption[]>>
  }
}

const QuarterlyLayout = <TInstruction extends unknown>(props: QuarterlyLayoutProps<TInstruction>) => {
  const heightHelper = useElementHeight();
  const { items, errors, initialLoading, isLoading, paginationComp, pagination } = props;

  const exportButton = props.exportOptions && <ExportButton
    canExportList={props.exportOptions.canExportList}
    instruction={props.instruction}
    exportOptionsApi={props.exportOptions.exportOptionsApi}
  />;

  if (!isLoading && errors.length > 0) {
    return <NoDataAvailableWarning>
      {errors[0].message}
    </NoDataAvailableWarning>
  }

  return <div className="s1_panel">
    <PanelSegment>
      <PanelSegment.Header size="h2">
        <PanelSegment.Title title={`${RESX.Appointment.Appointments} : ${pagination?.total || 0}`} />
        <PanelSegment.Pagination>
          {!initialLoading && paginationComp}
        </PanelSegment.Pagination>
        {exportButton}
      </PanelSegment.Header>
    </PanelSegment>
    {isLoading && <Loading height={heightHelper.height} dontAlignCenter />}
    {!isLoading && items.length === 0 && <NoDataAvailableDiv />}

    {items.length > 0 && <div ref={heightHelper.ref}>
      <QuarterlyAppointmentList items={items} />
    </div>}

    <div className="flex justify-end">
      {!initialLoading && paginationComp}
    </div>
  </div>
}

interface CalendarLayoutProps {
  filterSet: server.dto.FilterSets;
  items: server.dto.AppointmentsForDay[];
  fetchDayFunction: (day: moment.Moment) => AxiosPromise<server.dto.ApiResponseWrapperWithRequest<server.dto.AppointmentForPlanner[], any>>
  maxEntriesPerColumn: number;
  initialLoading: boolean;
  isLoading: boolean;
  paginationComp: JSX.Element;
  pagination: server.dto.PaginationData;
}

const CalendarLayout = (props: CalendarLayoutProps) => {
  const heightHelper = useElementHeight();
  const { filterSet, items, fetchDayFunction, maxEntriesPerColumn, initialLoading, isLoading, paginationComp, pagination } = props;

  return <div className="s1_panel">
    <PanelSegment>
      <PanelSegment.Header size="h2">
        <PanelSegment.Title title={`${RESX.Appointment.Appointments} : ${pagination?.total || 0}`} />
        <PanelSegment.Pagination>
          {!initialLoading && paginationComp}
        </PanelSegment.Pagination>
      </PanelSegment.Header>
    </PanelSegment>
    {isLoading && <Loading height={heightHelper.height} dontAlignCenter />}
    {!isLoading && items.length === 0 && <NoDataAvailableDiv />}

    {items.length > 0 && <div ref={heightHelper.ref}>
      <CalendarTemplate
        items={items as unknown as server.dto.AppointmentsForDay[]}
        maxEntriesPerColumn={maxEntriesPerColumn}
        filterSet={filterSet}
        fetchFunction={fetchDayFunction}
      />
    </div>}

    <div className="flex justify-end">
      {!initialLoading && paginationComp}
    </div>
  </div>
}